import { loadLoaderSpline } from './spline.js';

export const loader = () => {
    const loaderEl = document.querySelector('.loader');
    const loaderContent = document.querySelector('.loader__content');
    const loaderSplineContainer = document.getElementById('loader-spline-container');
    const bannerSplineContainer = document.querySelector('.banner-spline-container'); // Get first banner spline container
    const header = document.querySelector('.header');
    const body = document.body;

    // If loader doesn't exist, ensure site renders normally
    if (!loaderEl || !loaderContent) {
        // Clean up any loader-related classes/styles that might have been added
        if (body) {
            body.classList.remove('loader-active');
            body.style.top = '';
        }
        // Ensure header is visible if it exists
        if (header) {
            gsap.set(header, { 
                opacity: 1,
                y: 0,
                clearProps: 'transform'
            });
        }
        // Ensure banner Spline is visible if it exists
        if (bannerSplineContainer) {
            gsap.set(bannerSplineContainer, { 
                opacity: 1,
                visibility: 'visible'
            });
            bannerSplineContainer.style.pointerEvents = 'auto';
        }
        return;
    }

    // Set header opacity to 0 and translateY to -100px initially
    if (header) {
        gsap.set(header, { 
            opacity: 0,
            y: -500
        });
    }

    // Split text into letters and wrap each letter in a span
    const text = loaderContent.textContent.trim();
    if (text) {
        loaderContent.innerHTML = '';
        text.split('').forEach(letter => {
            const span = document.createElement('span');
            span.className = 'loader__letter';
            span.textContent = letter;
            loaderContent.appendChild(span);
        });
    }

    const letters = document.querySelectorAll('.loader__letter');
    if (!letters.length) return;

    // Prevent page scrolling during loader
    body.classList.add('loader-active');
    const scrollY = window.scrollY;
    body.style.top = `-${scrollY}px`;

    // Hide banner Spline container initially (it will be shown separately when loader finishes)
    if (bannerSplineContainer) {
        gsap.set(bannerSplineContainer, { 
            opacity: 0,
            visibility: 'hidden'
        });
        bannerSplineContainer.style.pointerEvents = 'none';
    }
    
    // No other content is hidden - all sections and divs remain visible
    const elementsToHide = [];

    // Load loader Spline immediately (non-blocking)
    loadLoaderSpline(loaderSplineContainer);

    // Initially hide text content, show Spline
    gsap.set(loaderContent, { opacity: 0, visibility: 'hidden' });
    gsap.set(loaderSplineContainer, { opacity: 1, visibility: 'visible' });
    gsap.set(letters, { 
        opacity: 1,
        x: 0
    });

    // Function to check if page is fully loaded
    const isPageLoaded = () => {
        return document.readyState === 'complete' && 
               (typeof document.images === 'undefined' || 
                Array.from(document.images).every(img => img.complete));
    };

    // Function to proceed with loader completion
    const completeLoader = () => {
        // Move Spline from loader to banner with smooth transition
        // Only move if banner has a valid spline URL
        const bannerSplineUrl = bannerSplineContainer?.getAttribute('data-spline-url');
        const hasValidBannerSpline = bannerSplineUrl && bannerSplineUrl.trim() !== '';
        
        if (loaderSplineContainer && bannerSplineContainer && hasValidBannerSpline) {
            const splineViewer = loaderSplineContainer.querySelector('spline-viewer');
            const splineScript = loaderSplineContainer.querySelector('script');
            
            if (splineViewer) {
                // Wait for banner to be in DOM
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Get positions for smooth transition
                        const loaderRect = loaderSplineContainer.getBoundingClientRect();
                        const bannerRect = bannerSplineContainer.getBoundingClientRect();
                        
                        // Create a wrapper for smooth transition
                        const wrapper = document.createElement('div');
                        wrapper.style.position = 'fixed';
                        wrapper.style.left = loaderRect.left + 'px';
                        wrapper.style.top = loaderRect.top + 'px';
                        wrapper.style.width = loaderRect.width + 'px';
                        wrapper.style.height = loaderRect.height + 'px';
                        wrapper.style.zIndex = '10000';
                        wrapper.style.pointerEvents = 'none';
                        wrapper.style.overflow = 'hidden';
                        
                        // Move spline viewer to wrapper
                        splineViewer.style.width = '100%';
                        splineViewer.style.height = '100%';
                        wrapper.appendChild(splineViewer);
                        document.body.appendChild(wrapper);
                        
                        // Animate wrapper to banner position smoothly
                        gsap.to(wrapper, {
                            left: bannerRect.left + 'px',
                            top: bannerRect.top + 'px',
                            width: bannerRect.width + 'px',
                            height: bannerRect.height + 'px',
                            duration: 1.2,
                            ease: 'power2.inOut',
                            onComplete: () => {
                                // Reset spline viewer styles
                                splineViewer.style.width = '';
                                splineViewer.style.height = '';
                                
                                // Add script to banner if not already present
                                if (splineScript && !bannerSplineContainer.querySelector('script[src*="spline-viewer"]')) {
                                    const clonedScript = splineScript.cloneNode(true);
                                    bannerSplineContainer.appendChild(clonedScript);
                                }
                                
                                // Move spline viewer to banner container
                                bannerSplineContainer.appendChild(splineViewer);
                                
                                // Make banner Spline visible
                                gsap.set(bannerSplineContainer, { 
                                    opacity: 1,
                                    visibility: 'visible'
                                });
                                bannerSplineContainer.style.pointerEvents = 'auto';
                                
                                // Remove wrapper
                                wrapper.remove();
                                
                                // Add settling animation to Spline
                                gsap.fromTo(bannerSplineContainer, 
                                    {
                                        scale: 1.02,
                                        opacity: 0.98
                                    },
                                    {
                                        scale: 1,
                                        opacity: 1,
                                        duration: 0.6,
                                        ease: 'power2.out',
                                        onComplete: () => {
                                            // Dispatch event when Spline has settled
                                            document.dispatchEvent(new CustomEvent('splineSettled'));
                                        }
                                    }
                                );
                            }
                        });
                        
                        // Fade in other main content (excluding header and banner which are already visible)
                        elementsToHide.forEach(el => {
                            gsap.set(el, { visibility: 'visible' });
                            gsap.to(el, {
                                opacity: 1,
                                duration: 1,
                                ease: 'power2.out'
                            });
                        });
                        
                        // Fade out the loader background to reveal banner
                        gsap.to(loaderEl, {
                            opacity: 0,
                            visibility: 'hidden',
                            duration: 1,
                            ease: 'power2.in',
                            onComplete: () => {
                                // Hide loader completely with display none
                                loaderEl.style.display = 'none';
                                // Re-enable page scrolling
                                body.classList.remove('loader-active');
                                body.style.top = '';
                                window.scrollTo(0, scrollY);
                                // Animate header in with smooth slide down animation
                                if (header) {
                                    gsap.to(header, {
                                        opacity: 1,
                                        y: 0,
                                        duration: 1.2,
                                        ease: 'power3.out',
                                        onComplete: () => {
                                            // Remove transform property from CSS after animation completes
                                            gsap.set(header, { clearProps: 'transform' });
                                        }
                                    });
                                }
                                // Dispatch custom event to signal loader completion
                                document.dispatchEvent(new CustomEvent('loaderCompleted'));
                            }
                        });
                    });
                });
            } else {
                // No Spline viewer found, fade in other main content (header and banner already visible)
                // Hide banner Spline container if it exists but has no valid URL
                if (bannerSplineContainer && !hasValidBannerSpline) {
                    gsap.set(bannerSplineContainer, { 
                        opacity: 0,
                        visibility: 'hidden',
                        display: 'none'
                    });
                    bannerSplineContainer.style.pointerEvents = 'none';
                }
                
                elementsToHide.forEach(el => {
                    gsap.set(el, { visibility: 'visible' });
                    gsap.to(el, {
                        opacity: 1,
                        duration: 0.8,
                        ease: 'power2.out'
                    });
                });
                
                gsap.to(loaderEl, {
                    opacity: 0,
                    visibility: 'hidden',
                    duration: 0.8,
                    ease: 'power2.in',
                    onComplete: () => {
                        // Hide loader completely with display none
                        loaderEl.style.display = 'none';
                        body.classList.remove('loader-active');
                        body.style.top = '';
                        window.scrollTo(0, scrollY);
                        // No Spline, so animate header in with smooth slide down animation
                        if (header) {
                            gsap.to(header, {
                                opacity: 1,
                                y: 0,
                                duration: 1.2,
                                ease: 'power3.out',
                                onComplete: () => {
                                    // Remove transform property from CSS after animation completes
                                    gsap.set(header, { clearProps: 'transform' });
                                }
                            });
                        }
                        document.dispatchEvent(new CustomEvent('loaderCompleted'));
                        document.dispatchEvent(new CustomEvent('splineSettled'));
                    }
                });
            }
        } else {
            // No banner Spline container found OR banner has no valid Spline URL
            // Hide banner Spline container if it exists but has no valid URL
            if (bannerSplineContainer && !hasValidBannerSpline) {
                gsap.set(bannerSplineContainer, { 
                    opacity: 0,
                    visibility: 'hidden',
                    display: 'none'
                });
                bannerSplineContainer.style.pointerEvents = 'none';
            }
            
            // Fade in other main content (header and banner already visible)
            elementsToHide.forEach(el => {
                gsap.set(el, { visibility: 'visible' });
                gsap.to(el, {
                    opacity: 1,
                    duration: 0.8,
                    ease: 'power2.out'
                });
            });
            
            gsap.to(loaderEl, {
                opacity: 0,
                visibility: 'hidden',
                duration: 0.8,
                ease: 'power2.in',
                onComplete: () => {
                    // Hide loader completely with display none
                    loaderEl.style.display = 'none';
                    body.classList.remove('loader-active');
                    body.style.top = '';
                    window.scrollTo(0, scrollY);
                    // No Spline, so animate header in with smooth slide down animation
                    if (header) {
                        gsap.to(header, {
                            opacity: 1,
                            y: 0,
                            duration: 1.2,
                            ease: 'power3.out',
                            onComplete: () => {
                                // Remove transform property from CSS after animation completes
                                gsap.set(header, { clearProps: 'transform' });
                            }
                        });
                    }
                    document.dispatchEvent(new CustomEvent('loaderCompleted'));
                    document.dispatchEvent(new CustomEvent('splineSettled'));
                }
            });
        }
    };

    // Create main timeline
    const tl = gsap.timeline();

    // Show Spline first (already visible)
    // Wait 4 seconds before showing INDEX text
    tl.to({}, { duration: 4 })
      // Show text content after 4 seconds
      .set(loaderContent, { 
          visibility: 'visible',
          opacity: 1
      })
      // Wait a brief moment before animating letters
      .to({}, { duration: 0.5 })
      // Spread letters apart with equal gap from center (all at the same time)
      .to(letters, {
          x: (i) => {
              const centerIndex = (letters.length - 1) / 2;
              const spacing = 20; // 20px gap between each letter
              return (i - centerIndex) * spacing;
              // For 5 letters: i=-40px, n=-20px, d=0px, e=20px, x=40px
          },
          duration: 2.5,
          ease: 'power3.out'
          // No stagger - all letters animate simultaneously from center
      })
      // Fade out the text content, but keep loader background visible
      .to(loaderContent, {
          opacity: 0,
          duration: 0.5,
          ease: 'power2.in'
      })
      // Wait for page to fully load before completing loader
      .call(() => {
          // Check if page is already loaded
          if (isPageLoaded()) {
              // Page is loaded, wait additional 1 second before completing
              setTimeout(() => {
                  completeLoader();
              }, 0);
          } else {
              // Wait for page to load
              const checkPageLoad = () => {
                  if (isPageLoaded()) {
                      // Page is loaded, wait additional 1 second before completing
                      setTimeout(() => {
                          completeLoader();
                      }, 0);
                  } else {
                      // Check again after a short delay
                      setTimeout(checkPageLoad, 100);
                  }
              };
              
              // Also listen for load event as backup
              window.addEventListener('load', () => {
                  // Wait additional 1 second after page loads
                  setTimeout(() => {
                      completeLoader();
                  }, 0);
              }, { once: true });
              
              // Start checking
              checkPageLoad();
              
              // Fallback: proceed after maximum wait time (10 seconds) + 1 second
              setTimeout(() => {
                  completeLoader();
              }, 1200);
          }
      });
}